import React from "react";
import { PageHero } from "../components/PageHero";
import { Reveal, Stagger, StaggerItem, Overline, SectionShell } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { TEAM_LEADERSHIP, TEAM_EXTENDED, VALUES, IMAGES } from "../content";
import { CLIENTS } from "../clients";

const initials = (name) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero
        overline="ABOUT DESTINYCS"
        title="An international firm,"
        highlight="built on spatial truth."
        desc="DCS is an internationally recognized IT firm offering geospatial and software solutions to clients across utilities, communications, natural resources, land & infrastructure, navigation, location-based services, insurance and funding agencies."
        image={IMAGES.hero_earth}
      />

      <SectionShell className="pt-4">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Reveal><Overline>WHO WE ARE</Overline></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-3 leading-tight">
                The entire geospatial value chain, under one roof
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <img
                src="/logo-full.png"
                alt="DestinyCS company logo"
                data-testid="about-logo"
                className="mt-6 h-20 sm:h-24 w-auto rounded-2xl bg-white/95 p-3"
              />
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 text-slate-300 leading-relaxed">
                DCS excels in delivering comprehensive solutions throughout the entire geospatial
                value chain — data creation, conversion, enhancement, software development,
                modeling, analytics and consulting. What sets the company apart is its unique
                capability to provide integrated solutions that follow data from raw capture to
                business decision.
              </p>
              <p className="mt-4 text-slate-400 leading-relaxed text-sm">
                A remarkably diverse workforce, comprising individuals with unique skill sets,
                domain expertise and functional capabilities — headquartered in Kakinada,
                Andhra Pradesh, serving clients worldwide.
              </p>
            </Reveal>
          </div>
          <Stagger className="space-y-4">
            {VALUES.map((v, i) => (
              <StaggerItem key={v.title}>
                <div className="border border-white/10 rounded-2xl bg-[#111622]/70 p-6 flex gap-5 items-start hover:border-cyan-500/40 transition-colors" data-testid={`value-card-${i}`}>
                  <span className="font-display text-3xl font-extrabold text-cyan-400/40">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white">{v.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </SectionShell>

      <SectionShell id="team">
        <Reveal><Overline>LEADERSHIP</Overline></Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-3 mb-12">
            The team behind the data
          </h2>
        </Reveal>
        <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {TEAM_LEADERSHIP.map((m) => (
            <StaggerItem key={m.name}>
              <div
                className="card-spotlight border border-white/10 rounded-2xl bg-[#111622]/70 p-6 text-center hover:border-cyan-500/40 hover:-translate-y-1 transition-all duration-300"
                data-testid={`team-member-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-cyan-400/25 to-blue-600/25 border border-cyan-400/20 flex items-center justify-center">
                  <span className="font-display text-lg font-bold text-cyan-300">{initials(m.name)}</span>
                </div>
                <h3 className="mt-4 font-display text-sm sm:text-base font-bold text-white leading-snug">{m.name}</h3>
                <p className="mt-1 font-mono-tech text-[10px] tracking-wider text-cyan-400/80 uppercase">{m.role}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.1}>
          <div className="mt-10 border border-white/5 rounded-2xl bg-white/[0.02] p-6">
            <p className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500 uppercase mb-4">Wider team — GIS engineers, QA, HR & operations</p>
            <div className="flex flex-wrap gap-2">
              {TEAM_EXTENDED.map((n) => (
                <span key={n} className="text-xs text-slate-400 bg-white/[0.04] border border-white/5 rounded-full px-3 py-1.5">{n}</span>
              ))}
            </div>
          </div>
        </Reveal>
      </SectionShell>

      <SectionShell id="clients">
        <div className="text-center mb-12">
          <Reveal><Overline>OUR CUSTOMERS</Overline></Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-3">
              Trusted by esteemed brands
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
              Our clients are our top priority — leaders in mapping, navigation, GIS and
              location intelligence worldwide.
            </p>
          </Reveal>
        </div>
        <Stagger className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="client-logo-wall">
          {CLIENTS.map((c) => (
            <StaggerItem key={c.name}>
              <div
                className="card-spotlight group border border-white/10 rounded-2xl bg-[#111622]/70 px-6 py-8 text-center hover:border-cyan-500/40 hover:-translate-y-1 transition-all duration-300"
                data-testid={`client-logo-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <p className="font-display text-lg sm:text-xl font-extrabold tracking-tight text-slate-300 group-hover:text-cyan-300 transition-colors">
                  {c.name}
                </p>
                <p className="mt-1.5 font-mono-tech text-[10px] tracking-[0.22em] text-slate-500 uppercase group-hover:text-cyan-500/80 transition-colors">
                  {c.sub}
                </p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </SectionShell>

      <CtaSection />
    </main>
  );
}
